/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { ButtonBase, Icon } from "../../core";
import { useFieldSectionContext } from "../../../contexts";
import { FieldRow } from "../FieldRow";

import "./FieldSectionHeader.scss";

export interface IFieldSectionHeaderProps {
	children?: React.ReactNode;
	className?: string;
	disabled?: boolean;
	controls?: React.ReactNode;
	collapsedControls?: React.ReactNode;
}

const FieldSectionHeader: React.FC<IFieldSectionHeaderProps> = ({
	children,
	className,
	disabled,
	controls,
	collapsedControls
}) => {
	const fieldSectionCtx = useFieldSectionContext();
	const root = !!fieldSectionCtx?.root;
	const group = !!fieldSectionCtx?.group;
	const collapsible = fieldSectionCtx?.collapsible;
	const collapsed = fieldSectionCtx?.collapsed;
	const toggleDisabled = !collapsible || disabled;

	const baseClass = BEM("edt-field-section-header", null, {
		collapsible,
		collapsed,
		disabled: toggleDisabled,
		root,
		group
	});

	const handleOnClick = useCallback(() => {
		fieldSectionCtx?.toggleCollapsed();
	}, [ fieldSectionCtx ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<FieldRow className={baseClass.toClassName()}>
			{collapsible ? (
				<ButtonBase
					disabled={toggleDisabled}
					className={baseClass.element("collapse-btn").toClassName()}
					onClick={handleOnClick}
				>
					<Icon
						name={root && group ? "mdi/chevron-down" : "mdi/menu-down"}
						className={baseClass.element("collapse-icon").toClassName()}
					/>

					{group && children}
				</ButtonBase>
			) : (
				children
			)}
			{collapsible && !group && children}
			{collapsed ? collapsedControls || controls : controls}
		</FieldRow>
	);
};

const FieldSectionHeaderMemo = React.memo(FieldSectionHeader);

export { FieldSectionHeaderMemo as FieldSectionHeader };
